import React, {useEffect, useRef, useState} from 'react';
import {Box, Button, Typography, useMediaQuery, useTheme} from '@mui/material';
import instance from '../../../instance';
import KKMFilters from './KKMFilters';
import KKMTable from './KKMTable';
import RegisterMerchantPopup from './RegisterMerchantPopup';
import PlusIcon from '../../Icons/PlusIcon';
import CallNotification from '../../../context/NotificationContext';
import MerchantsAnalytics from './MerchantAnalytics';
import Stack from '@mui/material/Stack';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import {EServiceRent} from '../../../Enums/ServiceRent';
import {ISaveBase64CSV, SaveBase64CSVToFile} from '../../StdFunction';
import {EServiceSign} from '../../../Enums/ServiceSign';

const KKMControl = ({userId}: {userId: number | null}) => {
    const [kkmListData, setKkmListData] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [registerPopupOpen, setRegisterPopupOpen] = useState<boolean>(false);
    const [filters, setFilters] = useState({merchantSearch: '', kkmSearch: ''});
    const hasFetchedRef = useRef(false);
    const {handleErrorNotification} = CallNotification();

    const theme = useTheme();
    const isSm = useMediaQuery(theme.breakpoints.down('sm'));

    const fetchKkmList = async () => {
        setIsLoading(true);
        try {
            const response = await instance.post('/LK', {
                method: 'GetKKMList',
                params: {
                    user_id: userId,
                },
            });
            handleErrorNotification(response);
            setKkmListData(response.data?.merchant_list || []);
        } catch (error: any) {
            console.log('Ошибка GetKKMList', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!userId || hasFetchedRef.current) return;
        hasFetchedRef.current = true;
        void fetchKkmList();
    }, [userId]);

    const handleRegisterSuccess = async (newUserId: number) => {
        setRegisterPopupOpen(false);

        try {
            const response = await instance.post('/LK', {
                method: 'GetMerchant',
                params: {
                    user_id: newUserId,
                },
            });

            handleErrorNotification(response);

            if (response.data?.merchant) {
                const newMerchant = {
                    ...response.data.merchant,
                    kkm_list: [],
                    _justAdded: true, // Метка для подсветки
                };

                setKkmListData(prev => {
                    const ownOrg = prev.find(m => m.id === userId);
                    const others = prev.filter(m => m.id !== userId);
                    return [ownOrg, newMerchant, ...others.filter(m => m.id !== newUserId)];
                });

                // Убрать подсветку через 2 секунды
                setTimeout(() => {
                    setKkmListData(prev =>
                        prev.map(m => {
                            if (m.id === newUserId) {
                                const {_justAdded, ...rest} = m;
                                return rest;
                            }
                            return m;
                        }),
                    );
                }, 2000);
            }
        } catch (error: any) {
            console.error('Ошибка GetMerchant:', error);
        }
    };

    const updateKkmItem = (merchantId: number, updatedKkm: any) => {
        setKkmListData(prev =>
            prev.map(merchant =>
                merchant.id === merchantId
                    ? {
                          ...merchant,
                          kkm_list: merchant.kkm_list.map((kkm: any) => (kkm.kkm_id === updatedKkm.kkm_id ? updatedKkm : kkm)),
                      }
                    : merchant,
            ),
        );
    };

    const addKkmItem = (merchantId: number, newKkm: any) => {
        setKkmListData(prev =>
            prev.map(merchant =>
                merchant.id === merchantId
                    ? {
                          ...merchant,
                          kkm_list: [{...newKkm, _justAdded: true}, ...(merchant.kkm_list || [])],
                      }
                    : merchant,
            ),
        );

        // Снять подсветку
        setTimeout(() => {
            setKkmListData(prev =>
                prev.map(merchant =>
                    merchant.id === merchantId
                        ? {
                              ...merchant,
                              kkm_list: merchant.kkm_list.map((kkm: any) => (kkm._justAdded ? {...kkm, _justAdded: undefined} : kkm)),
                          }
                        : merchant,
                ),
            );
        }, 2000);
    };

    const deleteKkmItem = (merchantId: number, kkmId: number) => {
        setKkmListData(prev =>
            prev.map(merchant =>
                merchant.id === merchantId
                    ? {
                          ...merchant,
                          kkm_list: merchant.kkm_list.filter((kkm: any) => kkm.kkm_id !== kkmId),
                      }
                    : merchant,
            ),
        );
    };

    const updateMerchant = (updated: any) => {
        setKkmListData(prev => prev.map(m => (m.id === updated.id ? {...m, ...updated} : m)));
    };

    const exportKKM = () => {
        if (!kkmListData.length) return;

        // Заголовки CSV
        const headers = [
            'Наименование мерчанта',
            'БИН/ИИН',
            'Телефон',
            'E-mail',
            'Плательщик НДС',
            'Модель',
            'РНМ',
            'ЗНМ',
            'ОФД',
            'Место торговли',
            'Тарифы',
            'Баланс',
            'Оплачено до',
        ];

        // Вспомогательная функция для определения тарифов
        const getServiceRentText = (kkm: any): string => {
            const rentBase = kkm.service_rent?.find((item: any) => item.service_rent_id === EServiceRent.Base);
            const rentNomenclature = kkm.service_rent?.find((item: any) => item.service_rent_id === EServiceRent.Nomenclature);
            const gasStation = kkm.service_sign?.find((item: any) => item.service_sign_id === EServiceSign.GasStation);

            const services: string[] = [];

            // Функция для проверки, что дата еще не истекла
            const isDateValid = (dateStr?: string): boolean => {
                if (!dateStr) return true; // Если даты нет, считаем тариф активным
                try {
                    const finishDate = new Date(dateStr);
                    const currentDate = new Date();
                    return finishDate >= currentDate;
                } catch {
                    return true; // При ошибке парсинга считаем тариф активным
                }
            };

            // Проверяем наличие различных тарифов, исключая истекшие
            if (rentBase && isDateValid(rentBase.finish_date)) {
                services.push('Прокси');
            }

            if (rentNomenclature && isDateValid(rentNomenclature.finish_date)) {
                services.push('Номенклатура');
            }

            if (gasStation && isDateValid(gasStation.finish_date)) {
                services.push('АЗС');
            }

            return services.join(', ');
        };

        // Функция для форматирования баланса
        const formatBalance = (kkm: any): string => {
            if (
                kkm.create_payment_info?.balance_control_disabled ||
                (kkm.create_payment_info?.payment_amount_by_rent === 0 && !kkm.create_payment_info?.upcoming_payment_amount)
            ) {
                return '0';
            }
            return kkm.balance_value?.toString() || '0';
        };

        // Подготовка данных
        const rows = kkmListData.flatMap(merchant => {
            // Если у мерчанта нет касс, все равно добавляем одну строку с данными мерчанта
            if (!merchant.kkm_list || merchant.kkm_list.length === 0) {
                return [
                    [
                        merchant.name || '',
                        merchant.inn || '',
                        merchant.phone || '',
                        merchant.e_mail || '',
                        merchant.flag_vat === 'Y' ? 'Да' : 'Нет',
                        '', // Модель
                        '', // РНМ
                        '', // ЗНМ
                        '', // ОФД
                        '', // Место торговли
                        '', // Тарифы
                        '', // Баланс
                        '', // Оплачено до
                    ],
                ];
            }

            // Для каждого мерчанта с кассами создаем строки для каждой кассы
            return merchant.kkm_list.map((kkm: any) => [
                merchant.name || '',
                merchant.inn || '',
                merchant.phone || '',
                merchant.e_mail || '',
                merchant.flag_vat === 'Y' ? 'Да' : 'Нет',
                kkm.model || '',
                kkm.reg_number || '',
                kkm.serial_number || '',
                kkm.ofd?.name || '',
                kkm.department?.name || kkm.department?.address || '',
                getServiceRentText(kkm),
                formatBalance(kkm),
                kkm.create_payment_info?.paid_until_date || '',
            ]);
        });

        // Создаем CSV контент
        const csvContent = [headers.join(';'), ...rows.map(row => row.map((field: any) => `"${field}"`).join(';'))].join('\n');

        // Преобразуем в base64
        const base64Content = btoa(unescape(encodeURIComponent(csvContent)));

        const prop: ISaveBase64CSV = {
            base64: base64Content,
            filename: `kkm_export_${new Date().toISOString().split('T')[0]}`,
            ext: 'csv',
            encoding: 'utf-8',
        };
        SaveBase64CSVToFile(prop);
    };

    return (
        <Box p={isSm ? 1 : 2} pb={isSm ? 8 : 12}>
            <Stack
                direction={isSm ? 'column' : 'row'}
                spacing={isSm ? 1 : 2}
                justifyContent="space-between"
                alignItems="center"
                mb={3}
                mt={isSm ? 1 : 0}
                gap={isSm ? 1 : 0}
            >
                <Button
                    startIcon={<PlusIcon />}
                    onClick={() => setRegisterPopupOpen(true)}
                    sx={{zIndex: 10}}
                    fullWidth={isSm}
                    variant="contained"
                >
                    Добавить мерчанта
                </Button>

                <Stack direction={isSm ? 'column' : 'row'} spacing={1} alignItems="center" sx={{width: isSm ? '100%' : 'auto'}}>
                    <KKMFilters filters={filters} setFilters={setFilters} />
                    <Button
                        variant="outlined"
                        sx={{
                            border: '1px solid #D1DAE3',
                            borderRadius: '6px',
                            backgroundColor: '#fff',
                            height: '44px',
                            color: '#33415B',
                            '&:hover': {
                                backgroundColor: '#f0f0f0',
                                border: '1px solid #D1DAE3',
                            },
                        }}
                        fullWidth={isSm}
                        startIcon={<FileUploadIcon />}
                        onClick={exportKKM}
                        disabled={isLoading}
                    >
                        <Typography variant="body2">ЭКСПОРТ</Typography>
                    </Button>
                </Stack>
            </Stack>

            <MerchantsAnalytics merchants={kkmListData} />

            <KKMTable
                data={kkmListData}
                serviceCenterId={userId}
                filters={filters}
                isLoading={isLoading}
                onKkmUpdate={updateKkmItem}
                onKkmCreate={addKkmItem}
                onKkmDelete={deleteKkmItem}
                onMerchantUpdate={updateMerchant}
            />

            <RegisterMerchantPopup
                open={registerPopupOpen}
                onClose={() => setRegisterPopupOpen(false)}
                onSuccess={newUserId => void handleRegisterSuccess(newUserId)}
                serviceCenterId={userId}
            />
        </Box>
    );
};

export default KKMControl;
